/** Helpers for gifting-page and give-a-gift share flows. */

export function isMobileDevice() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

export function canUseNativeShare(shareData) {
    if (typeof navigator.share !== 'function' || !isMobileDevice()) {
        return false;
    }
    if (typeof navigator.canShare === 'function') {
        return navigator.canShare(shareData);
    }
    return true;
}

export function getGiftingPageUrl(slug, wfItemId) {
    return wfItemId
        ? `https://www.greatestgiftapp.com/gifts/${slug}`
        : `https://app.greatestgiftapp.com/registry/${slug}`;
}

export function buildDefaultShareMessage(childName) {
    return `Hey, we're saving for ${childName}'s future! Here's a link for you to invest in ${childName}'s future through Greatest Gift.`;
}

export function getGiveAGiftShareUrl(childName, user) {
    const params = new URLSearchParams();
    params.set('Gift_for', childName);
    if (user?.first_name) params.set('Send_to', user.first_name);
    if (user?.email) params.set('send_to_email', user.email);
    params.set('channel', 'email');
    return `https://app.GreatestGiftApp.com/give-a-gift?${params.toString()}`;
}

export function buildMailtoHref(message, url) {
    return `mailto:?body=${encodeURIComponent(`${message}\n\n${url}`)}`;
}

export function buildSmsHref(message, url) {
    return `sms:?&body=${encodeURIComponent(`${message}\n\n${url}`)}`;
}

const DEFAULT_QR_FILENAME = 'gg-registry-qr.png';

/** Lowercase, hyphenated child name safe for a download filename. */
export function sanitizeChildNameForFilename(childName) {
    if (!childName || typeof childName !== 'string') return '';

    return childName
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/'/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/** gg-registry-qr-lux-dennis.png, or gg-registry-qr.png when the name is unusable. */
export function registryQrFilename(childName) {
    const slug = sanitizeChildNameForFilename(childName);
    return slug ? `gg-registry-qr-${slug}.png` : DEFAULT_QR_FILENAME;
}

/**
 * Desktop saves the PNG. Mobile opens the system share sheet with the file
 * when it can, otherwise opens the image so the owner can long-press to save.
 * Returns false when the owner dismisses the share sheet.
 */
export async function deliverRegistryQr(blob, childName) {
    const filename = registryQrFilename(childName);
    const file = new File([blob], filename, { type: 'image/png' });
    const canShareFile = isMobileDevice()
        && typeof navigator.share === 'function'
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] });

    if (canShareFile) {
        try {
            await navigator.share({ files: [file], title: 'Registry QR code' });
            return true;
        } catch (err) {
            if (err && err.name === 'AbortError') return false;
        }
    }

    const url = URL.createObjectURL(blob);

    // A phone that cannot share a file gets the image in a new tab.
    if (isMobileDevice()) {
        const opened = window.open(url, '_blank');
        if (opened) return true;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return true;
}

/** Opens the native share sheet on mobile; returns true if share completed or user dismissed. */
export async function openNativeShare({ url, text, title, onChannelSelect }) {
    const shareData = { url, text, title };
    if (!canUseNativeShare(shareData)) {
        return false;
    }

    try {
        await navigator.share(shareData);
        if (onChannelSelect) onChannelSelect('native');
        return true;
    } catch (err) {
        // User closed the sheet — not an error.
        if (err?.name === 'AbortError') {
            return true;
        }
        return false;
    }
}
